/**
 * gldn-cart-atc-instrument — observability for the cart-sync (Workstream A)
 * and false-sold-out (Workstream B) bugs. gldn23/handoff #10.
 *
 * READ-ONLY: this never mutates the cart, never preventDefaults, and never
 * alters behavior. Every Clarity call goes through window.gldnReport (a safe
 * queueing bridge) and the whole file is wrapped in defensive try/catch so a
 * reporting failure can never break add-to-cart or checkout.
 *
 * It hooks the existing Horizon event contract + the rendered DOM — NO core
 * Horizon file is modified:
 *   - cart_state_check (drawer DOM count vs /cart.js) fires ONLY on
 *       drawer-open (authoritative async compare) and checkout-button click
 *       (synchronous best-effort compare against the cached server count,
 *       since the page is navigating).
 *   - atc_sold_out / atc_error captures the VERBATIM Shopify message on
 *       cart:error (the theme already surfaces it; we just count it).
 *   - atc_success on a product-form add (CartAddEvent), as the success baseline.
 *   - preorder_atc_intercepted / success / error for the PreProduct cart
 *       handoff added by gldn-preproduct-cart-bridge.js (gldn85 / #364).
 *   - session tags on load: cart_token_tail, cart_item_count_at_load,
 *       cart_empty_with_token, cart_token_age_hours_approx (localStorage proxy,
 *       since Shopify does not expose cart created_at to the storefront).
 *
 * Real selectors/events confirmed against the theme (handoff investigation):
 *   authoritative drawer count = cart-items-component[data-drawer] [ref="cartItemCount"]
 *   drawer open event          = 'dialog:open' on <cart-drawer-component> (non-bubbling → capture)
 *   ATC error                  = 'cart:error' (detail.data.message)
 *   ATC success                = 'cart:update' with detail.data.source === 'product-form-component'
 */
(function () {
  'use strict';

  var report = window.gldnReport || function () {};
  var SOLDOUT_RE = /(sold out|out of stock|not available|unavailable)/i;

  // Authoritative server item_count, cached from the most recent /cart.js read
  // (page load + each drawer-open). The checkout-click handler can't await a
  // fetch before the redirect, so it compares the drawer DOM against this cache.
  var lastServerCount = null;

  function drawerDomCount() {
    try {
      var el =
        document.querySelector('cart-items-component[data-drawer] [ref="cartItemCount"]') ||
        document.querySelector('cart-items-component [ref="cartItemCount"]');
      if (!el) return null;
      var n = parseInt((el.textContent || '').trim(), 10);
      return isNaN(n) ? null : n;
    } catch (e) {
      return null;
    }
  }

  function emitStateCheck(trigger, serverCount) {
    try {
      var domCount = drawerDomCount();
      report('set', 'cart_check_trigger', String(trigger));
      report('set', 'cart_drawer_count', String(domCount));
      report('set', 'cart_js_count', String(serverCount));
      if (domCount != null && serverCount != null && domCount !== serverCount) {
        report('event', 'cart_state_mismatch');
      } else {
        report('event', 'cart_state_ok');
      }
    } catch (e) {}
  }

  // --- Drawer open: authoritative async compare (drawer stays open, no nav) ---
  // 'dialog:open' does not bubble, so listen in the capture phase and match the
  // cart drawer specifically (the same event fires for other dialogs).
  document.addEventListener(
    'dialog:open',
    function (e) {
      try {
        var t = e.target;
        if (!t || !t.matches || !t.matches('cart-drawer-component')) return;
        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            lastServerCount = cart.item_count;
            emitStateCheck('drawer_open', cart.item_count);
          })
          .catch(function () {
            report('event', 'cart_state_fetch_failed');
          });
      } catch (e2) {}
    },
    true
  );

  // --- Checkout click: synchronous best-effort compare (page is navigating) ---
  // Capture phase so the event is recorded before any redirect/handler runs.
  // Observation only — does not interfere with gldn-gift-note's checkout handler.
  document.addEventListener(
    'click',
    function (e) {
      try {
        var target = e.target;
        if (!target || !target.closest) return;
        var hit = target.closest(
          '#checkout, .cart__checkout-button, [name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart, shop-pay-button'
        );
        if (!hit) return;
        var accelerated = !!hit.closest(
          '.additional-checkout-buttons, shopify-accelerated-checkout-cart, shop-pay-button'
        );
        // Synchronous compare against the cached server count so the event lands
        // before the redirect. (Authoritative server count is captured at the
        // preceding drawer-open; this catches a drawer that diverged since.)
        emitStateCheck('checkout_' + (accelerated ? 'accelerated' : 'standard'), lastServerCount);
      } catch (e2) {}
    },
    true
  );

  // --- ATC error: capture the verbatim Shopify message (Workstream B) ---
  document.addEventListener('cart:error', function (e) {
    try {
      var d = (e.detail && e.detail.data) || e.detail || {};
      var msg = (d.message || d.description || '').toString();
      report('set', 'atc_error_message', msg.slice(0, 200));
      if (SOLDOUT_RE.test(msg)) {
        report('event', 'atc_sold_out');
      } else {
        report('event', 'atc_error');
      }
    } catch (e2) {}
  });

  // --- ATC success baseline + keep the server-count cache fresh ---
  document.addEventListener('cart:update', function (e) {
    try {
      var d = (e.detail && e.detail.data) || {};
      if (typeof d.itemCount === 'number') lastServerCount = d.itemCount;
      if (d.source === 'product-form-component') report('event', 'atc_success');
    } catch (e2) {}
  });

  // --- PreProduct ATC handoff outcome (gldn85 / #364) ---
  document.addEventListener('gldn:preproduct-atc', function (e) {
    try {
      var d = (e && e.detail) || {};
      var state = String(d.state || 'unknown');
      report('set', 'preorder_atc_state', state);
      if (d.variantId) report('set', 'preorder_atc_variant_id', String(d.variantId));
      if (d.message) report('set', 'preorder_atc_error_message', String(d.message).slice(0, 200));
      if (state === 'intercepted' || state === 'success' || state === 'error') {
        report('event', 'preorder_atc_' + state);
      }
    } catch (e2) {}
  });

  // --- Session tags on load: token + count + approximate token age ---
  // Shopify exposes cart.token + item_count but NOT created_at, so token age is
  // approximated by stamping (per-token) the first time we observe it on this
  // device. cart_empty_with_token is the strong stale-token candidate signal.
  function tagSession() {
    try {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          lastServerCount = cart.item_count;
          report('set', 'cart_item_count_at_load', String(cart.item_count));
          var token = cart.token || '';
          report('set', 'cart_token_tail', token ? token.slice(-8) : 'none');
          report('set', 'cart_empty_with_token', cart.item_count === 0 && token ? 'true' : 'false');
          try {
            var KEY = 'gldn_cart_token_seen';
            var now = Date.now();
            if (!token) {
              report('set', 'cart_token_age_hours_approx', 'no_token');
              return;
            }
            var rec = {};
            try {
              rec = JSON.parse(localStorage.getItem(KEY) || '{}');
            } catch (e) {}
            if (rec.token !== token) {
              rec = { token: token, firstSeen: now };
              localStorage.setItem(KEY, JSON.stringify(rec));
            }
            var ageH = Math.round((now - (rec.firstSeen || now)) / 3600000);
            report('set', 'cart_token_age_hours_approx', String(ageH));
          } catch (e3) {}
        })
        .catch(function () {});
    } catch (e4) {}
  }

  // Schedule the one network call (session-tag /cart.js read) for idle time
  // AFTER load, so it never competes with LCP/INP — matches the deferred-work
  // pattern used by the Clarity loader + gldn-web-vitals reporter (#173/#215).
  function scheduleTagSession() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(tagSession, { timeout: 5000 });
    } else {
      setTimeout(tagSession, 0);
    }
  }
  if (document.readyState === 'complete') scheduleTagSession();
  else window.addEventListener('load', scheduleTagSession, { once: true });
})();
